import { Component } from '@verndale/core';
import Pristine from 'pristinejs';
import serialize from 'form-serialize';
import MicroModal from 'micromodal';
import axios from 'axios';
import { constEvent } from '../utils/constants';

import {
  PRISTINE_SETTINGS,
  SERIALIZE_SETTINGS
} from '../utils/default-settings';

class Module extends Component {
  async setupDefaults() {
    this.dom = {
      $documentsLinks: this.el._$$('.document-listing__element-button'),
      $modalForm: this.el._$('.safety-data-sheet-modal__form'),
      $modalFormBtnSubmit: this.el._$('.safety-data-sheet-modal__button'),
      $modalFormErrorMessage: this.el._$(
        '.safety-data-sheet-modal__form-error-message'
      ),
      $modalSelects: this.el._$$('.slim-select'),
      $modalAtccNo: this.el._$('.safety-data-sheet-modal__atcc-no'),
      $modalSdsRequest: this.el._$('.safety-data-sheet-request-modal'),
      $sdsCloseTrigger: _$$(
        '.safety-data-sheet-modal [data-generic-modal-close]'
      )
    };
    this.closeTrigger = 'data-generic-modal-close';
    this.openClass = 'generic-modal--open';
    this.endpoint = this.dom.$modalForm?.getAttribute('action') || null;

    this.pristineForm = null;
    this.currentModalId = null;

    // Load essential libraries asynchronously
    const [Pristine, MicroModal, axios] = await Promise.all([
      import('pristinejs').then(module => module.default),
      import('micromodal').then(module => module.default),
      import('axios').then(module => module.default)
    ]);

    // Initialize
    this.init(Pristine, MicroModal, axios);
  } //setupDefaults

  addListeners(MicroModal, axios) {
    const { $documentsLinks, $modalSelects, $modalForm, $sdsCloseTrigger } = this.dom;

    document._on(
      constEvent.SHOW_SDS_MODAL,
      this.openModalDynamically.bind(this, MicroModal)
    );
    $documentsLinks?._on('click', this.openModal.bind(this, MicroModal));
    $modalSelects?._on('change', this.validateSelects.bind(this));
    $modalForm?._on('submit', this.submitForm.bind(this, axios));
    $sdsCloseTrigger?._on('click', this.forceCloseModal.bind(this, MicroModal));
  } //addListeners

  init(Pristine, MicroModal, axios) {
    this.pristineInit(Pristine);
    this.addListeners(MicroModal, axios);
  } //init

  pristineInit(Pristine) {
    const { $modalForm } = this.dom;
    if ($modalForm) {
      this.pristineForm = new Pristine($modalForm, PRISTINE_SETTINGS);
    }
  } //pristineInit

  openModal(MicroModal, e) {
    e.preventDefault();
    const modalId = e.target.getAttribute('data-modal-id') || null;
    this.currentModalId = modalId;

    if (modalId) {
      MicroModal.show(modalId, {
        closeTrigger: this.closeTrigger,
        openClass: this.openClass,
        disableScroll: true,
        awaitOpenAnimation: true,
        awaitCloseAnimation: true,
        onClose: modal => {
          this.currentModalId = null;
          this.resetModalForm();
        }
      });
    }
  } //openModal

  openModalDynamically(e) {
    e.preventDefault();
    const modalId = e.detail.id;
    this.currentModalId = modalId;
    this.updateContent(e.detail.SDSModal);

    if (modalId) {
      MicroModal.show(modalId, {
        closeTrigger: this.closeTrigger,
        openClass: this.openClass,
        disableScroll: true,
        awaitOpenAnimation: true,
        awaitCloseAnimation: true,
        onClose: modal => {
          this.currentModalId = null;
          this.resetModalForm();
        }
      });
    }
  } //openModal

  validateSelects(e) {
    this.enableDisableSubmitBtn();

    this.pristineForm.validate(e.target);
  } //validateSelects

  enableDisableSubmitBtn() {
    const { $modalFormBtnSubmit } = this.dom;

    try {
      const valid = this.pristineForm.validate(true);
      $modalFormBtnSubmit.disabled = !valid;
    } catch (error) {
      return;
    }
  } //enableDisableSubmitBtn

  submitForm(e) {
    e.preventDefault();
    const {
      $modalForm,
      $modalFormErrorMessage,
      $modalFormBtnSubmit,
      $modalSdsRequest
    } = this.dom;
    const valid = this.pristineForm.validate();

    if (valid) {
      $modalFormBtnSubmit.disabled = true;
      $modalFormErrorMessage.style.display = 'none';
      const formData = serialize($modalForm, SERIALIZE_SETTINGS);

      axios({
        method: 'post',
        url: this.endpoint,
        data: formData,
        headers: {
          'content-type': 'application/json'
        }
      })
        .then(response => {
          const { success, body, message } = response.data;

          this.resetModalForm();

          if (success) {
            const path = body?.path;

            window.open(path, '_blank');
          } else {
            const sdsModalId = $modalSdsRequest.getAttribute('id') || null;

            if (sdsModalId) {
              MicroModal.show(sdsModalId, {
                closeTrigger: this.closeTrigger,
                openClass: this.openClass,
                disableScroll: true,
                awaitOpenAnimation: true,
                awaitCloseAnimation: true
              });
            }
          }
        })
        .finally(() => {
          $modalFormBtnSubmit.disabled = true;
        });
    } else {
      this.validateInputs();
    }
  } //submitForm

  validateInputs() {
    try {
      const errors = this.pristineForm.getErrors();
      for (const error of errors) {
        const input = error.input;
        this.pristineForm.validate(input);
      }
    } catch (error) {
      return;
    }
  } //validateInputs

  resetModalForm() {
    const { $modalFormErrorMessage, $modalForm } = this.dom;
    const $dropdowns = $modalForm._$$('select');

    try {
      $modalFormErrorMessage.style.display = 'none';

      $dropdowns.forEach(dropdown => {
        dropdown.selectedIndex = 0;
        const options = dropdown.innerHTML;
        dropdown.innerHTML = options;
      });

      this.pristineForm.reset();
    } catch (error) {
      return;
    }
  } //resetModalForm

  updateContent(data) {
    const select = this.dom.$modalSelects[0];
    let languages = data.SDSLanguagesAvailable.map(l => ({
      text: l.Name,
      value: l.Code
    }));
    languages = [
      { placeholder: true, text: data.SelectLanguageLabel, value: '' },
      ...languages
    ];
    select.slim.setData(languages);
    this.dom.$modalAtccNo.value = data.ATCCNumber;
  }

  forceCloseModal(e) {
    if (
      this.currentModalId &&
      e.target.hasAttribute('data-generic-modal-close')
    ) {
      e?.preventDefault();
      e?.stopPropagation();

      MicroModal.close(this.currentModalId);
    }
  } //forceCloseModal
}
export default Module;
